// IP 合作谈判「授权信息」表格（ProTablePlusForm，可复用内包/外包场景）。
// 行模型对齐 CoreTermTable：条款类别(授权区域/授权时长) | 网易方出价 | 回复 + 批量编辑 | 意见反馈。
// 授权区域(license_regions)/授权时长(license_duration) 各为独立谈判条款（后端 CORE_TERMS section），
// 回复/意见列 + 批量编辑标题复用 NegotiationTable/renderCells 工厂，数据收集走外层 editState（key=item_key）。
// 「网易方出价」列参考 BudgetOfferTable 状态变更驱动：编辑态用 IPCooperationDemandFormModel 渲染既有 form model 字段
// （license_regions 多选 / auth_duration 单选）的 SFormItem，经 Form.Item 注册受控 value/onChange，编辑值自动写回 form 对应 key
// （getFieldsValue 可返回、提交可上送）；只读态渲染派生文本（getAuthInfoOfferValue），外包/非商务修改不回归。

import React, { forwardRef, memo, useMemo } from 'react';
import { ProTablePlusForm } from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import type {
  IpCooperationDemandDetailVoL2Info,
  OptionItemVo,
} from '#services/api';
import FormModelCell, { type FormModelCellConf } from './FormModelCell';
import {
  ActionCell,
  getOpinionTitle,
  OpinionCell,
  createRenderBatchEditTitle,
  type NegotiationEditState,
  type NegotiationItem,
} from './renderCells';
import RoundDiffCell from '../RoundDiff/RoundDiffCell';
import type { CoreTermDiffMap, CoreTermItemDiffMap } from '../RoundDiff/types';
import { AUTH_DURATION_OPTIONS } from '../utils';

// 授权信息固定字段（约定前端写死）：授权区域 / 授权时长
export const AUTH_INFO_ITEM_KEYS = ['license_regions', 'license_duration'];
// 授权信息中文名（后端 item_name 缺失时兜底）
const AUTH_INFO_NAME_MAP: Record<string, string> = {
  license_regions: '授权区域',
  license_duration: '授权时长',
};

/** 出价列可编辑单元格字段渲染配置（复用共享 FormModelCell 的配置类型；itemCols/renderType 由 FormModelCell 内部固定） */
type OfferModelConf = FormModelCellConf;

export interface AuthorizationInfoTableProps {
  /** 谈判条款列表（negotiation_items，内部取授权信息 2 字段） */
  items?: NegotiationItem[];
  /** 二级单信息（l2_info，网易方出价从 license_regions/auth_info.license_duration 派生） */
  l2?: IpCooperationDemandDetailVoL2Info;
  /** 编辑状态（key=item_key） */
  editState?: Record<string, NegotiationEditState>;
  /** 是否可编辑 */
  editable?: boolean;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（仅作用于本表格条款） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 发行区域选项（Apollo region.options 动态下发，供授权地区 code→中文名映射） */
  countryRegionOptions?: OptionItemVo[];
  /** 场景模式：external=外包（内容列总宽 700 对齐三表）；internal/缺省=内包（保持现状） */
  mode?: 'external' | 'internal';
  /** 出价列可编辑（仅内包商务修改编辑态开启；外包/只读缺省 false，出价列恒只读展示派生值） */
  offerEditable?: boolean;
  /** 出价列编辑字段渲染配置（offerEditable=true 时用 FormModel 渲染 SFormItem 受控字段；缺省用 l2 派生只读展示） */
  modelConf?: OfferModelConf;
  /** 历史轮次对比 diff（key=item_key：license_regions/license_duration）；传了则出价列改渲染 RoundDiffCell（最新值着色 + 历史值删除线），不传保持原展示 */
  diffMap?: CoreTermDiffMap;
  /** 历史轮次对比 diff（回复/意见列，key=item_key）：版权方回复与意见反馈对比用；传了则 ActionCell/OpinionCell 渲染 diff */
  itemDiffMap?: CoreTermItemDiffMap;
  /** 对比态开关：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；SecondaryDetailContent 传 compareActive */
  compareEnabled?: boolean;
}

/** 出价列只读 diff 单元格（顶层稳定组件，避免内联闭包被 cellEditMemo 判定组件类型变化重挂载）：
 *  有 diff 走 RoundDiffCell（newValue/oldValue 已由 roundDiff 归一化为中文名），无 diff 展示 getAuthInfoOfferValue 派生文本 */
const OfferValueDiffCell: React.FC<{
  offerValue?: string;
  diff?: CoreTermDiffMap[string];
}> = ({ offerValue, diff }) =>
  diff ? (
    <RoundDiffCell diff={diff} />
  ) : (
    <span className="text-color-1">{offerValue || '-'}</span>
  );

// 网易方出价：从 l2_info 派生（授权区域 code→中文名；授权时长 M3→3个月）
const getAuthInfoOfferValue = (
  key: string,
  l2?: IpCooperationDemandDetailVoL2Info,
  countryRegionOptions?: OptionItemVo[]
): string => {
  if (!l2) return '';
  switch (key) {
    case 'license_regions': {
      // 授权地区 code→中文名：Apollo region.options 动态映射（country_region_options），未知 code 保留原值
      const opts = countryRegionOptions || [];
      return (l2.license_regions || [])
        .map((r) => opts.find((o) => o.value === r)?.name || r)
        .join('/');
    }
    case 'license_duration': {
      const v = l2.auth_info?.license_duration;
      return (
        (AUTH_DURATION_OPTIONS.find((o) => o.value === v)?.name || v) ?? ''
      );
    }
    default:
      return '';
  }
};

const AuthorizationInfoTableInner: React.FC<AuthorizationInfoTableProps> = (
  props
) => {
  const {
    items = [],
    l2,
    editState = {},
    editable = false,
    onEditChange,
    onBatchAction,
    countryRegionOptions,
    mode,
    offerEditable = false,
    modelConf,
    diffMap,
    itemDiffMap,
    compareEnabled,
  } = props;
  // 对比态开关门控：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；diffMap/itemDiffMap 仅在对比态被读取
  const activeDiffMap = compareEnabled ? diffMap : undefined;
  const activeItemDiffMap = compareEnabled ? itemDiffMap : undefined;
  // 详情条款按 item_key 索引（回显 action/opinion）
  const itemsByKey = useMemo(() => {
    const map: Record<string, NegotiationItem> = {};
    (items || []).forEach((i) => {
      if (i.item_key) map[i.item_key] = i;
    });
    return map;
  }, [items]);

  // 固定 2 字段平铺行（网易方出价从 l2_info 派生）
  const rows = useMemo(
    () =>
      AUTH_INFO_ITEM_KEYS.map((key) => {
        const item = itemsByKey[key];
        return {
          __row_key__: key,
          item_key: key,
          // 条款类别中文优先：后端 item_name 可能存英文字段名（license_regions 等），固定字段用前端中文映射兜底
          item_name: AUTH_INFO_NAME_MAP[key] || item?.item_name,
          offer_value: getAuthInfoOfferValue(key, l2, countryRegionOptions),
        };
      }),
    [itemsByKey, l2, countryRegionOptions]
  );

  // 批量编辑标题（列头 Popover，仅针对本表格授权信息 2 字段）
  const renderBatchEditTitle = createRenderBatchEditTitle({
    itemsByKey,
    editState,
    editable,
    onBatchAction,
  });

  // 4 列：条款类别 / 网易方出价 / 回复 / 意见反馈（对齐 CoreTermTable 结构）
  const columns = useMemo<AddGroupFormColumn[]>(
    () => [
      {
        dataIndex: 'item_name',
        title: '条款类别',
        width: 140,
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: { customComp: () => <span>{row.item_name}</span> },
        }),
      },
      {
        dataIndex: 'offer_value',
        title: '网易方出价',
        // 外包内容列总宽统一 700（条款140+出价560，对齐三表回复列 x）；内包保持现状 460
        // 内包只读态（非商务修改，offerEditable=false）放大出价列到 500（140+500=640），
        // 使回复/意见列与权益表格对齐；商务修改编辑态（offerEditable）保持 460。
        width: mode === 'external' ? 560 : offerEditable ? 460 : 500,
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: {
            // 编辑态（offerEditable + modelConf）参考 BudgetOfferTable 状态变更驱动：用共享 FormModelCell 顶层稳定组件
            // 渲染既有 form model 字段（license_regions 多选 / auth_duration 单选）的 SFormItem，经 Form.Item 注册
            // 受控 value/onChange，编辑值自动写回 form 对应 key（getFieldsValue 可返回、提交可上送）。
            // 只读态：对比态（compareActive）+ 有 diff → 顶层稳定 OfferValueDiffCell 走 RoundDiffCell（最新值着色/历史值删除线）；
            // 非对比态（外包/非商务修改）→ 同一稳定组件无 diff 分支复用 getAuthInfoOfferValue 派生文案，不回归。
            ...(offerEditable && modelConf
              ? {
                  customComp: FormModelCell,
                  // 变体全部走 props（formItemKey 原始值引用比较恒等；modelConf 建议调用方 useMemo 稳定）
                  formItemKey:
                    row?.item_key === 'license_regions'
                      ? 'license_regions'
                      : 'auth_duration',
                  modelConf,
                }
              : {
                  customComp: OfferValueDiffCell,
                  offerValue: row?.offer_value,
                  diff: activeDiffMap?.[row?.item_key],
                  row,
                }),
          },
        }),
      },
      {
        dataIndex: 'action',
        title: renderBatchEditTitle(AUTH_INFO_ITEM_KEYS),
        // 回复列固定宽（走查：编辑态 180px 放三个 Medium Tag / 只读态 100px 放单 Tag）
        width: editable ? 180 : 100,
        // 外包走查 recZIyp：回复列右侧描边缺失（意见反馈列弹性无宽，verticalBorder 不渲染该侧边框）→ 回复列 th/td 加右边框（仅外包模式，内包保持现状）
        ...(mode === 'external'
          ? {
              onCell: () => ({
                style: { borderRight: '1px solid rgb(223, 224, 229)' },
              }),
              onHeaderCell: () => ({
                style: { borderRight: '1px solid rgb(223, 224, 229)' },
              }),
            }
          : {}),
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: {
            customComp: ActionCell,
            row,
            itemsByKey,
            editState,
            editable,
            readOnly: !editable,
            onEditChange,
            // 历史轮次对比态：回复 diff（readOnly 时 ActionCell 渲染新旧对比）
            actionDiff: activeItemDiffMap?.[row?.item_key]?.action,
          },
        }),
      },
      {
        dataIndex: 'opinion',
        title: getOpinionTitle(),
        // 外包意见反馈/说明列不设固定宽（弹性撑满，最小宽 100 防坍缩，对齐上线联动明细）；内包保持现状
        // 内包意见列固定 400（W 合计=1140 对齐权益表格；原无宽弹性导致缩放比不同，回复/意见列错位）
        ...(mode === 'external' ? { minWidth: 100 } : { width: 400 }),
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: {
            customComp: OpinionCell,
            row,
            itemsByKey,
            editState,
            editable,
            readOnly: !editable,
            onEditChange,
            // 历史轮次对比态：意见反馈 diff（readOnly 时 OpinionCell 渲染新旧对比）
            opinionDiff: activeItemDiffMap?.[row?.item_key]?.opinion,
          },
        }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      itemsByKey,
      editState,
      editable,
      onEditChange,
      onBatchAction,
      mode,
      offerEditable,
      modelConf,
      activeDiffMap,
      activeItemDiffMap,
    ]
  );

  return (
    <ProTablePlusForm
      value={rows}
      onChange={() => {}}
      columns={columns}
      rowKey="__row_key__"
      verticalBorder
      scroll={{ x: '100%' }}
      // 表格级 readOnly = 出价列与回复列都不可编辑时才锁死。
      // 内包商务修改（offerEditable=true, editable=false）：出价列可编辑但回复只读 → readOnly=false 放行出价列 Select 交互；
      // 内包只读（两都 false）→ true；外包（editable=true, offerEditable=false）→ false 放行回复列，出价列由 OfferValueCell 内部 offerEditable 门控仍只读。
      readOnly={!editable && !offerEditable}
    />
  );
};

// FormModel 字段渲染（SFormItem/Form.Item）会注入 ref；展示型表格无需真实 ref，
// forwardRef 接受并忽略（对齐 CoreTermTable 范式）。memo 包裹隔离父组件无关重绘
const AuthorizationInfoTable = memo(
  forwardRef<unknown, AuthorizationInfoTableProps>((props, ref) => {
    void ref;
    return <AuthorizationInfoTableInner {...props} />;
  })
);

export default AuthorizationInfoTable;
